<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="1300" scrollable>
    <v-card>
      <v-card-title class="text-subtitle-1 grey--text">
        <v-icon small class="mr-2" color="teal">mdi-chart-box-outline</v-icon>
        Analisi Arrivi — {{ anno }}
        <v-spacer />
        <v-btn icon small @click="$emit('input', false)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-0" style="height: 75vh;">

        <!-- Stato vuoto -->
        <div v-if="!calendario" class="pa-8 text-center grey--text">
          <v-icon large color="grey lighten-2">mdi-calendar-blank-outline</v-icon>
          <div class="mt-2 text-caption">Nessun dato disponibile per l'anno {{ anno }}</div>
        </div>

        <template v-else>
          <v-tabs v-model="tab" background-color="grey lighten-4">
            <v-tab>Riepilogo</v-tab>
            <v-tab>Per specie</v-tab>
            <v-tab>Per fornitore</v-tab>
          </v-tabs>
          <v-divider />

          <v-tabs-items v-model="tab">

            <!-- ─── Tab 1: Riepilogo ─── -->
            <v-tab-item>
              <div class="pa-4">
                <v-row>
                  <v-col v-for="kpi in kpi_riepilogo" :key="kpi.label" cols="6" sm="4">
                    <v-sheet outlined rounded class="pa-3 text-center">
                      <div class="text-overline grey--text">{{ kpi.label }}</div>
                      <div class="text-h5 font-weight-bold" :class="kpi.color + '--text'">{{ kpi.valore }}</div>
                      <div class="text-caption grey--text">{{ kpi.sub }}</div>
                    </v-sheet>
                  </v-col>
                </v-row>
              </div>
            </v-tab-item>

            <!-- ─── Tab 2: Per specie (card) ─── -->
            <v-tab-item>
              <div class="pa-4">
                <!-- Filtro nome -->
                <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
                  <v-text-field
                    v-model="filtro_nome"
                    dense
                    outlined
                    hide-details
                    clearable
                    placeholder="Filtra per nome…"
                    prepend-inner-icon="mdi-magnify"
                    :error="filtro_nome_regex_error"
                    style="max-width: 260px;"
                  />
                  <span class="text-caption grey--text">{{ per_specie_filtrata.length }} specie</span>
                </div>
                <div v-if="!per_specie_filtrata.length" class="text-center grey--text text-caption py-8">Nessun dato</div>
                <v-row v-else>
                  <!-- Colonna sinistra: Talea -->
                  <v-col cols="12" sm="6">
                    <div class="d-flex align-center mb-2" style="gap:6px;">
                      <v-icon small color="green">mdi-leaf</v-icon>
                      <span class="text-caption text-uppercase font-weight-bold grey--text">Talea</span>
                      <span class="text-caption grey--text">({{ per_specie_talea_filtrata.length }})</span>
                    </div>
                    <div v-if="!per_specie_talea_filtrata.length" class="text-caption grey--text py-4 text-center">Nessuna specie</div>
                    <v-card
                      v-for="sp in per_specie_talea_filtrata"
                      :key="sp.id"
                      outlined
                      class="mb-3"
                    >
                      <!-- Header specie -->
                      <div class="d-flex align-center px-3 py-2" style="background: #f5f5f5; border-bottom: 1px solid #e0e0e0;">
                        <v-avatar size="32" class="mr-2">
                          <v-img :src="avatarSpecie(sp.id)" />
                        </v-avatar>
                        <div class="flex-grow-1 min-width-0">
                          <div class="text-body-2 font-weight-medium text-truncate">{{ sp.nome }}</div>
                        </div>
                        <div class="text-right ml-2">
                          <div class="text-h6 font-weight-bold teal--text">{{ formatNum(sp.totale) }}</div>
                          <div class="text-caption grey--text">pz totali</div>
                        </div>
                      </div>
                      <!-- Corpo: settimane | fornitori -->
                      <v-row no-gutters>
                        <v-col cols="6">
                          <div class="px-3 pt-2 pb-1">
                            <div class="text-caption text-uppercase grey--text font-weight-bold mb-1">
                              <v-icon x-small class="mr-1">mdi-calendar-week</v-icon>Settimane
                            </div>
                            <div v-for="sett in sp.per_settimana" :key="sett.num" class="d-flex justify-space-between align-center py-1" style="border-bottom: 1px solid #f0f0f0;">
                              <span class="text-body-2 grey--text text--darken-1">Sett. {{ sett.num }}<span class="ml-1" style="color:#bdbdbd; font-size:12px;">{{ sett.range }}</span></span>
                              <span class="text-body-2 font-weight-medium">{{ formatNum(sett.qta) }} pz</span>
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="6">
                          <div class="px-3 pt-2 pb-1">
                            <div class="text-caption text-uppercase grey--text font-weight-bold mb-1">
                              <v-icon x-small class="mr-1">mdi-truck-delivery</v-icon>Fornitori
                            </div>
                            <div v-for="forn in sp.per_fornitore" :key="forn.id" class="d-flex align-center py-1" style="border-bottom: 1px solid #f0f0f0;">
                              <v-avatar size="20" class="mr-1 flex-shrink-0"><v-img :src="avatarFornitore(forn.id)" /></v-avatar>
                              <span class="text-body-2 grey--text text--darken-1 text-truncate flex-grow-1">{{ forn.nome }}</span>
                              <span class="text-body-2 font-weight-medium ml-1 flex-shrink-0">{{ formatNum(forn.qta) }} pz</span>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>

                  <!-- Colonna destra: Seme -->
                  <v-col cols="12" sm="6">
                    <div class="d-flex align-center mb-2" style="gap:6px;">
                      <v-icon small color="brown lighten-2">mdi-seed</v-icon>
                      <span class="text-caption text-uppercase font-weight-bold grey--text">Seme</span>
                      <span class="text-caption grey--text">({{ per_specie_seme_filtrata.length }})</span>
                    </div>
                    <div v-if="!per_specie_seme_filtrata.length" class="text-caption grey--text py-4 text-center">Nessuna specie</div>
                    <v-card
                      v-for="sp in per_specie_seme_filtrata"
                      :key="sp.id"
                      outlined
                      class="mb-3"
                    >
                      <!-- Header specie -->
                      <div class="d-flex align-center px-3 py-2" style="background: #f5f5f5; border-bottom: 1px solid #e0e0e0;">
                        <v-avatar size="32" class="mr-2">
                          <v-img :src="avatarSpecie(sp.id)" />
                        </v-avatar>
                        <div class="flex-grow-1 min-width-0">
                          <div class="text-body-2 font-weight-medium text-truncate">{{ sp.nome }}</div>
                        </div>
                        <div class="text-right ml-2">
                          <div class="text-h6 font-weight-bold teal--text">{{ formatNum(sp.totale) }}</div>
                          <div class="text-caption grey--text">pz totali</div>
                        </div>
                      </div>
                      <!-- Corpo: settimane | fornitori -->
                      <v-row no-gutters>
                        <v-col cols="6">
                          <div class="px-3 pt-2 pb-1">
                            <div class="text-caption text-uppercase grey--text font-weight-bold mb-1">
                              <v-icon x-small class="mr-1">mdi-calendar-week</v-icon>Settimane
                            </div>
                            <div v-for="sett in sp.per_settimana" :key="sett.num" class="d-flex justify-space-between align-center py-1" style="border-bottom: 1px solid #f0f0f0;">
                              <span class="text-body-2 grey--text text--darken-1">Sett. {{ sett.num }}<span class="ml-1" style="color:#bdbdbd; font-size:12px;">{{ sett.range }}</span></span>
                              <span class="text-body-2 font-weight-medium">{{ formatNum(sett.qta) }} pz</span>
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="6">
                          <div class="px-3 pt-2 pb-1">
                            <div class="text-caption text-uppercase grey--text font-weight-bold mb-1">
                              <v-icon x-small class="mr-1">mdi-truck-delivery</v-icon>Fornitori
                            </div>
                            <div v-for="forn in sp.per_fornitore" :key="forn.id" class="d-flex align-center py-1" style="border-bottom: 1px solid #f0f0f0;">
                              <v-avatar size="20" class="mr-1 flex-shrink-0"><v-img :src="avatarFornitore(forn.id)" /></v-avatar>
                              <span class="text-body-2 grey--text text--darken-1 text-truncate flex-grow-1">{{ forn.nome }}</span>
                              <span class="text-body-2 font-weight-medium ml-1 flex-shrink-0">{{ formatNum(forn.qta) }} pz</span>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
            </v-tab-item>

            <!-- ─── Tab 3: Per fornitore (card) ─── -->
            <v-tab-item>
              <div class="pa-4">
                <div v-if="!per_fornitore_dettaglio.length" class="text-center grey--text text-caption py-8">Nessun dato</div>
                <v-row v-else>
                  <v-col
                    v-for="forn in per_fornitore_dettaglio"
                    :key="forn.id"
                    cols="12" sm="6"
                  >
                    <v-card outlined class="fill-height">
                      <!-- Header fornitore -->
                      <div class="d-flex align-center px-3 py-2" style="background: #f5f5f5; border-bottom: 1px solid #e0e0e0;">
                        <v-avatar size="32" class="mr-2">
                          <v-img :src="avatarFornitore(forn.id)" />
                        </v-avatar>
                        <div class="flex-grow-1 min-width-0">
                          <div class="text-body-2 font-weight-medium text-truncate">{{ forn.nome }}</div>
                          <div class="text-caption grey--text">
                            {{ forn.n_settimane }} {{ forn.n_settimane === 1 ? 'settimana' : 'settimane' }}
                            &nbsp;·&nbsp;
                            {{ forn.per_specie.length }} {{ forn.per_specie.length === 1 ? 'specie' : 'specie' }}
                          </div>
                        </div>
                        <div class="text-right ml-2">
                          <div class="text-h6 font-weight-bold teal--text">{{ formatNum(forn.totale) }}</div>
                          <div class="text-caption grey--text">pz totali</div>
                        </div>
                      </div>

                      <!-- Corpo: 2 colonne -->
                      <v-row no-gutters class="fill-height">
                        <!-- Colonna specie -->
                        <v-col cols="6">
                          <div class="px-3 pt-2 pb-1">
                            <div class="text-caption text-uppercase grey--text font-weight-bold mb-1">
                              <v-icon x-small class="mr-1">mdi-sprout</v-icon>Specie
                            </div>
                            <!-- Seme -->
                            <template v-if="forn.specie_seme.length">
                              <div class="d-flex align-center mt-1 mb-1" style="gap:4px;">
                                <v-icon x-small color="brown lighten-2">mdi-seed</v-icon>
                                <span class="text-caption grey--text">seme</span>
                              </div>
                              <div
                                v-for="sp in forn.specie_seme"
                                :key="sp.id"
                                class="d-flex align-center py-1"
                                style="border-bottom: 1px solid #f0f0f0;"
                              >
                                <v-avatar size="20" class="mr-1 flex-shrink-0">
                                  <v-img :src="avatarSpecie(sp.id)" />
                                </v-avatar>
                                <span class="text-body-2 grey--text text--darken-1 text-truncate flex-grow-1">{{ sp.nome }}</span>
                                <span class="text-body-2 font-weight-medium ml-1 flex-shrink-0">{{ formatNum(sp.qta) }} pz</span>
                              </div>
                            </template>
                            <!-- Talea -->
                            <template v-if="forn.specie_talea.length">
                              <div class="d-flex align-center mt-2 mb-1" style="gap:4px;">
                                <v-icon x-small color="green">mdi-leaf</v-icon>
                                <span class="text-caption grey--text">talea</span>
                              </div>
                              <div
                                v-for="sp in forn.specie_talea"
                                :key="sp.id"
                                class="d-flex align-center py-1"
                                style="border-bottom: 1px solid #f0f0f0;"
                              >
                                <v-avatar size="20" class="mr-1 flex-shrink-0">
                                  <v-img :src="avatarSpecie(sp.id)" />
                                </v-avatar>
                                <span class="text-body-2 grey--text text--darken-1 text-truncate flex-grow-1">{{ sp.nome }}</span>
                                <span class="text-body-2 font-weight-medium ml-1 flex-shrink-0">{{ formatNum(sp.qta) }} pz</span>
                              </div>
                            </template>
                          </div>
                        </v-col>

                        <!-- Colonna settimane -->
                        <v-col cols="6">
                          <div class="px-3 pt-2 pb-1">
                            <div class="text-caption text-uppercase grey--text font-weight-bold mb-1">
                              <v-icon x-small class="mr-1">mdi-calendar-week</v-icon>Settimane
                            </div>
                            <div
                              v-for="sett in forn.per_settimana"
                              :key="sett.num"
                              class="d-flex justify-space-between align-center py-1"
                              style="border-bottom: 1px solid #f0f0f0;"
                            >
                              <span class="text-body-2 grey--text text--darken-1">Sett. {{ sett.num }}<span class="ml-1" style="color:#bdbdbd; font-size:12px;">{{ sett.range }}</span></span>
                              <span class="text-body-2 font-weight-medium">{{ formatNum(sett.qta) }} pz</span>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
            </v-tab-item>

          </v-tabs-items>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";

export default {
  name: "AnalisiArriviDialog",
  props: {
    value: { type: Boolean, default: false },
    anno:  { type: Number, required: true },
  },
  data() {
    return {
      tab: 0,
      filtro_prop: 'tutti',
      filtro_nome: '',
    };
  },
  computed: {
    calendario() {
      return this.$store.getters.get_calendario_arrivi(this.anno);
    },
    specie_per_id() {
      const map = {};
      for (const s of (this.$store.getters.get_specie || [])) map[s.id] = s;
      return map;
    },
    fornitori_per_id() {
      const map = {};
      for (const f of (this.$store.getters.get_fornitori || [])) map[f.id] = f;
      return map;
    },

    // ── flat: un record per arrivo ──────────────────────────────────────
    flat_arrivi() {
      if (!this.calendario?.settimane) return [];
      const result = [];
      for (const sett of this.calendario.settimane) {
        for (const arrivo of (sett.arrivi || [])) {
          result.push({
            id:            arrivo.id,
            id_fornitore:  arrivo.id_fornitore,
            num_settimana: sett.numero + 1,
            ha_pdf:        !!arrivo.pdf,
          });
        }
      }
      return result;
    },

    // ── flat: un record per prodotto in un arrivo ───────────────────────
    flat_prodotti() {
      if (!this.calendario?.settimane) return [];
      const result = [];
      for (const sett of this.calendario.settimane) {
        for (const arrivo of (sett.arrivi || [])) {
          for (const p of (arrivo.prodotti || [])) {
            const sp = this.specie_per_id[p.id_specie];
            result.push({
              num_settimana: sett.numero + 1,
              id_fornitore:  arrivo.id_fornitore,
              id_specie:     p.id_specie,
              qta:           Number(p.qta) || 0,
              propagazione:  sp?.propagazione || 0,
            });
          }
        }
      }
      return result;
    },

    // ── KPI riepilogo ───────────────────────────────────────────────────
    kpi_riepilogo() {
      const totale_pz      = this.flat_prodotti.reduce((s, p) => s + p.qta, 0);
      const sett_attive    = new Set(this.flat_arrivi.map((a) => a.num_settimana)).size;
      const fornitori_dist = new Set(this.flat_arrivi.map((a) => a.id_fornitore)).size;
      const specie_dist    = new Set(this.flat_prodotti.map((p) => p.id_specie)).size;
      const pdf_allegati   = this.flat_arrivi.filter((a) => a.ha_pdf).length;
      return [
        { label: "Pezzi totali",      valore: this.formatNum(totale_pz),      sub: "in tutti gli arrivi",       color: "deep-orange" },
        { label: "Settimane attive",  valore: sett_attive,                    sub: "con almeno 1 arrivo",       color: "teal"        },
        { label: "Arrivi totali",     valore: this.flat_arrivi.length,        sub: "record fornitore/sett.",    color: "primary"     },
        { label: "Fornitori",         valore: fornitori_dist,                 sub: "distinti",                  color: "purple"      },
        { label: "Specie",            valore: specie_dist,                    sub: "distinte",                  color: "green"       },
        { label: "PDF allegati",      valore: pdf_allegati,                   sub: "bolle di consegna",         color: "red"         },
      ];
    },

    // ── Seme vs Talea (totali) ──────────────────────────────────────────
    seme_talea() {
      const seme  = { pz: 0, specie: new Set() };
      const talea = { pz: 0, specie: new Set() };
      for (const p of this.flat_prodotti) {
        if (p.propagazione === 1) { seme.pz  += p.qta; seme.specie.add(p.id_specie);  }
        else if (p.propagazione === 2) { talea.pz += p.qta; talea.specie.add(p.id_specie); }
      }
      return {
        seme:      { pz: seme.pz,  specie: seme.specie.size  },
        talea:     { pz: talea.pz, specie: talea.specie.size },
        totale_pz: seme.pz + talea.pz,
      };
    },

    // ── Seme vs Talea per settimana ─────────────────────────────────────
    seme_talea_per_settimana() {
      const map = {};
      for (const p of this.flat_prodotti) {
        if (!map[p.num_settimana]) map[p.num_settimana] = { num: p.num_settimana, seme: 0, talea: 0 };
        if (p.propagazione === 1)      map[p.num_settimana].seme  += p.qta;
        else if (p.propagazione === 2) map[p.num_settimana].talea += p.qta;
      }
      return Object.values(map)
        .map((r) => ({ ...r, totale: r.seme + r.talea }))
        .sort((a, b) => a.num - b.num);
    },

    // ── Per settimana ───────────────────────────────────────────────────
    per_settimana() {
      const map = {};
      // Inizializza dai dati grezzi (include settimane con 0 prodotti ma ≥1 arrivo)
      for (const sett of (this.calendario?.settimane || [])) {
        if (!sett.arrivi?.length) continue;
        map[sett.numero + 1] = {
          num:       sett.numero + 1,
          n_arrivi:  sett.arrivi.length,
          seme: 0, talea: 0, totale: 0,
          pdf_count: sett.arrivi.filter((a) => !!a.pdf).length,
        };
      }
      for (const p of this.flat_prodotti) {
        const row = map[p.num_settimana];
        if (!row) continue;
        if (p.propagazione === 1)      row.seme  += p.qta;
        else if (p.propagazione === 2) row.talea += p.qta;
        row.totale += p.qta;
      }
      return Object.values(map)
        .sort((a, b) => a.num - b.num)
        .map((r) => ({ ...r, range: this.rangeSettimana(r.num) }));
    },

    picco_settimana() {
      if (!this.per_settimana.length) return null;
      return this.per_settimana.reduce(
        (best, r) => (r.totale > best.totale ? r : best),
        this.per_settimana[0]
      ).num;
    },

    // ── Per fornitore ───────────────────────────────────────────────────
    per_fornitore() {
      const map = {};
      for (const a of this.flat_arrivi) {
        if (!map[a.id_fornitore]) {
          map[a.id_fornitore] = { id: a.id_fornitore, settimane: new Set(), n_arrivi: 0, seme: 0, talea: 0, totale: 0, pdf_count: 0 };
        }
        map[a.id_fornitore].settimane.add(a.num_settimana);
        map[a.id_fornitore].n_arrivi++;
        if (a.ha_pdf) map[a.id_fornitore].pdf_count++;
      }
      for (const p of this.flat_prodotti) {
        if (!map[p.id_fornitore]) continue;
        if (p.propagazione === 1)      map[p.id_fornitore].seme  += p.qta;
        else if (p.propagazione === 2) map[p.id_fornitore].talea += p.qta;
        map[p.id_fornitore].totale += p.qta;
      }
      return Object.values(map)
        .map((r) => ({ ...r, n_settimane: r.settimane.size, nome: this.nomeFornitore(r.id) }))
        .sort((a, b) => b.totale - a.totale);
    },

    // ── Per specie dettaglio (settimane + fornitori) ─────────────────────────
    per_specie_dettaglio() {
      const map = {};
      for (const p of this.flat_prodotti) {
        const sid = p.id_specie;
        if (!map[sid]) {
          const sp = this.specie_per_id[sid];
          map[sid] = {
            id:           sid,
            nome:         sp?.nome || 'Specie #' + sid,
            propagazione: p.propagazione,
            totale:       0,
            sett_map:     {},
            forn_map:     {},
          };
        }
        map[sid].totale += p.qta;
        map[sid].sett_map[p.num_settimana] = (map[sid].sett_map[p.num_settimana] || 0) + p.qta;
        map[sid].forn_map[p.id_fornitore]  = (map[sid].forn_map[p.id_fornitore]  || 0) + p.qta;
      }
      return Object.values(map)
        .map((r) => ({
          ...r,
          per_settimana: Object.entries(r.sett_map)
            .map(([num, qta]) => ({ num: Number(num), qta, range: this.rangeSettimana(Number(num)) }))
            .sort((a, b) => a.num - b.num),
          per_fornitore: Object.entries(r.forn_map)
            .map(([id, qta]) => ({ id, qta, nome: this.nomeFornitore(id) }))
            .sort((a, b) => b.qta - a.qta),
        }))
        .sort((a, b) => b.totale - a.totale);
    },
    filtro_nome_regex_error() {
      if (!this.filtro_nome) return false;
      try { new RegExp(this.filtro_nome, 'i'); return false; } catch { return true; }
    },
    per_specie_filtrata() {
      let righe = this.per_specie_dettaglio;
      if (this.filtro_nome && !this.filtro_nome_regex_error) {
        const re = new RegExp(this.filtro_nome, 'i');
        righe = righe.filter((s) => re.test(s.nome));
      }
      return righe;
    },
    per_specie_talea_filtrata() {
      return this.per_specie_filtrata.filter((s) => s.propagazione !== 1);
    },
    per_specie_seme_filtrata() {
      return this.per_specie_filtrata.filter((s) => s.propagazione === 1);
    },

    // ── Per fornitore dettaglio (specie + settimane) ─────────────────────
    per_fornitore_dettaglio() {
      const map = {};
      for (const p of this.flat_prodotti) {
        const fid = p.id_fornitore;
        if (!map[fid]) {
          map[fid] = {
            id:          fid,
            nome:        this.nomeFornitore(fid),
            totale:      0,
            n_settimane: 0,
            sett_set:    new Set(),
            sett_map:    {},
            specie_map:  {},
          };
        }
        map[fid].totale += p.qta;
        map[fid].sett_set.add(p.num_settimana);
        map[fid].sett_map[p.num_settimana]  = (map[fid].sett_map[p.num_settimana]  || 0) + p.qta;
        if (!map[fid].specie_map[p.id_specie]) {
          const sp = this.specie_per_id[p.id_specie];
          map[fid].specie_map[p.id_specie] = { id: p.id_specie, nome: sp?.nome || 'Specie #' + p.id_specie, propagazione: p.propagazione, qta: 0 };
        }
        map[fid].specie_map[p.id_specie].qta += p.qta;
      }
      return Object.values(map)
        .map((r) => ({
          id:          r.id,
          nome:        r.nome,
          totale:      r.totale,
          n_settimane: r.sett_set.size,
          per_settimana: Object.entries(r.sett_map)
            .map(([num, qta]) => ({ num: Number(num), qta, range: this.rangeSettimana(Number(num)) }))
            .sort((a, b) => a.num - b.num),
          per_specie: Object.values(r.specie_map)
            .sort((a, b) => b.qta - a.qta),
          specie_seme: Object.values(r.specie_map)
            .filter((s) => s.propagazione === 1)
            .sort((a, b) => b.qta - a.qta),
          specie_talea: Object.values(r.specie_map)
            .filter((s) => s.propagazione !== 1)
            .sort((a, b) => b.qta - a.qta),
        }))
        .sort((a, b) => b.totale - a.totale);
    },
  },
  methods: {
    formatNum(n) {
      return (Number(n) || 0).toLocaleString("it-IT");
    },
    percSul(parte, totale) {
      if (!totale) return "0%";
      return Math.round((parte / totale) * 100) + "%";
    },
    rangeSettimana(num) {
      const start = moment().isoWeek(num).startOf("isoWeek");
      const end   = moment().isoWeek(num).endOf("isoWeek");
      return start.format("D MMM") + " – " + end.format("D MMM");
    },
    nomeFornitore(id) {
      const f = this.fornitori_per_id[id];
      return f ? f.nome : "Fornitore #" + id;
    },
    avatarFornitore(id) {
      return process.env.VUE_APP_API_ROOT + "/fornitore/" + id + "/avatar";
    },
    avatarSpecie(id) {
      const sp = this.specie_per_id[id];
      return `${process.env.VUE_APP_API_ROOT}/specie/${id}/img?md5=${sp?.img_md5 || ""}`;
    },
  },
};
</script>

<style scoped>
.min-width-0 {
  min-width: 0;
}
</style>
